import React, { useState, useEffect, useRef } from "react";
import C from "./theme";
import storage from "./storage";
import { ALL_CONFERENCES, ALL_TEAMS, MOCK_GAMES } from "./data";

// ─── Helpers ───

const MO = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DY = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const SMO = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtS(d) { return `${DY[d.getDay()]}, ${SMO[d.getMonth()]} ${d.getDate()}`; }
function same(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function addD(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function ord(n) { const s = ["th","st","nd","rd"]; const v = n % 100; return s[(v-20)%10] || s[v] || s[0]; }

function parseTime(t) {
  if (!t) return 9999;
  const m = (t || '').match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!m) return 9999;
  let h = parseInt(m[1]);
  const min = parseInt(m[2]);
  const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

function sortGames(games) {
  const order = { live: 0, pre: 1, final: 2, cancelled: 3 };
  return [...games].sort((a, b) => {
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    if (a.status === "live") return (b.inning || 0) - (a.inning || 0);
    const aT = parseTime(a.time || a.startTime);
    const bT = parseTime(b.time || b.startTime);
    return aT - bT;
  });
}

function validateEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
function validatePhone(p) { return /^\+?[\d\s\-()]{10,}$/.test(p.replace(/\s/g, "")); }
function formatPhone(v) {
  const d = v.replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}

const inputStyle = {
  width: "100%", fontFamily: "'Inter',sans-serif", fontSize: "13px",
  padding: "8px 10px", borderRadius: "6px", border: `1px solid ${C.inputBorder}`,
  background: C.inputBg, color: C.text, outline: "none",
};
const labelStyle = { fontFamily: "'Inter',sans-serif", fontSize: "11px", fontWeight: 600, color: C.dim, marginBottom: "4px", display: "block" };
const errStyle = { fontFamily: "'Inter',sans-serif", fontSize: "10px", color: C.red, marginTop: "2px" };

// ─── Auth Modal ───

function AuthModal({ mode, onClose, onAuth, onSwitch }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isSignup = mode === "signup";

  const validate = () => {
    const e = {};
    if (isSignup) {
      if (!name.trim()) e.name = "Name is required";
      if (!email.trim()) e.email = "Email is required";
      else if (!validateEmail(email)) e.email = "Enter a valid email";
      if (!phone.trim()) e.phone = "Phone number is required";
      else if (!validatePhone(phone)) e.phone = "Enter a valid 10-digit phone number";
      if (!username.trim()) e.username = "Username is required";
      else if (username.length < 3) e.username = "Min 3 characters";
      if (!password) e.password = "Password is required";
      else if (password.length < 8) e.password = "Min 8 characters";
      if (password !== confirm) e.confirm = "Passwords do not match";
    } else {
      if (!username.trim()) e.username = "Required";
      if (!password) e.password = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onAuth({ name, email, phone, username, password, isSignup });
    }, 800);
  };

  const field = (label, val, setVal, type, placeholder, err) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input style={inputStyle} type={type === "phone" ? "text" : (type || "text")} value={val}
        onChange={e => setVal(type === "phone" ? formatPhone(e.target.value) : e.target.value)}
        placeholder={placeholder} />
      {err && <div style={errStyle}>{err}</div>}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: C.overlay, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: "10px", width: "380px", maxHeight: "90vh", overflowY: "auto", padding: "24px" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: "18px", fontWeight: 700, color: C.bright, margin: 0 }}>{isSignup ? "Create Account" : "Sign In"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.dim, fontSize: "18px", cursor: "pointer" }}>{"\u2715"}</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {isSignup && field("Full Name", name, setName, "text", "John Smith", errors.name)}
          {isSignup && field("Email", email, setEmail, "email", "john@example.com", errors.email)}
          {isSignup && field("Phone Number", phone, setPhone, "phone", "(555) 123-4567", errors.phone)}
          {field("Username", username, setUsername, "text", "johndoe", errors.username)}
          {field("Password", password, setPassword, "password", isSignup ? "Min 8 characters" : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022", errors.password)}
          {isSignup && field("Confirm Password", confirm, setConfirm, "password", "Re-enter password", errors.confirm)}
        </div>
        <button onClick={handleSubmit} disabled={submitting} style={{ width: "100%", marginTop: "20px", padding: "10px", borderRadius: "6px", border: "none", background: C.pillGreen, color: "#fff", fontFamily: "'Inter',sans-serif", fontSize: "14px", fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "..." : (isSignup ? "Create Account" : "Sign In")}
        </button>
        <div style={{ marginTop: "14px", textAlign: "center", fontSize: "12px", color: C.dim }}>
          {isSignup ? "Already have an account? " : "Don't have an account? "}
          <span onClick={onSwitch} style={{ color: C.blue, cursor: "pointer", fontWeight: 600 }}>{isSignup ? "Sign in" : "Create one"}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Favorites Panel ───

function FavoritesPanel({ favorites, setFavorites, defaultView, setDefaultView, onClose }) {
  const [search, setSearch] = useState("");
  const [cf, setCf] = useState("All");
  const confs = ["All", ...new Set(ALL_TEAMS.map(t => t.conf))].sort((a, b) => a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b));
  const filtered = ALL_TEAMS.filter(t => {
    if (cf !== "All" && t.conf !== cf) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.abbr.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name));
  const toggle = abbr => setFavorites(prev => prev.includes(abbr) ? prev.filter(f => f !== abbr) : [...prev, abbr]);

  return (
    <div style={{ position: "fixed", inset: 0, background: C.overlay, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: "10px", width: "480px", maxHeight: "85vh", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.divider}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: "16px", fontWeight: 700, color: C.bright, margin: 0 }}>{"\u2605"} My Teams</h2>
            <button onClick={onClose} style={{ background: "none", border: "none", color: C.dim, fontSize: "18px", cursor: "pointer" }}>{"\u2715"}</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", color: C.dim }}>Default view:</span>
            {["all", "favorites"].map(v => (
              <button key={v} onClick={() => setDefaultView(v)} style={{
                fontSize: "10px", fontWeight: 600, padding: "3px 8px", borderRadius: "10px", border: "none",
                background: defaultView === v ? (v === "all" ? C.pillBlue : C.pillPurple) : C.pillGray,
                color: defaultView === v ? "#fff" : C.pillGrayText, cursor: "pointer",
              }}>{v === "all" ? "All Games" : "\u2605 My Teams"}</button>
            ))}
          </div>
          <input style={{ ...inputStyle, marginBottom: "8px" }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search teams..." />
          <div style={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
            {confs.map(c => (
              <button key={c} onClick={() => setCf(c)} style={{
                fontSize: "9px", fontWeight: 600, padding: "2px 6px", borderRadius: "8px", border: "none",
                background: cf === c ? C.pillBlue : C.pillGray, color: cf === c ? "#fff" : C.pillGrayText, cursor: "pointer",
              }}>{c}</button>
            ))}
          </div>
        </div>
        {favorites.length > 0 && (
          <div style={{ padding: "8px 20px", borderBottom: `1px solid ${C.divider}`, flexShrink: 0 }}>
            <div style={{ fontSize: "10px", color: C.dim, marginBottom: "4px" }}>{favorites.length} selected</div>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {favorites.map(abbr => (
                <span key={abbr} onClick={() => toggle(abbr)} style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "10px", fontWeight: 600, color: "#fff",
                  background: C.pillPurple, padding: "2px 6px", borderRadius: "8px", cursor: "pointer",
                }}>{abbr} {"\u2715"}</span>
              ))}
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 16px" }}>
          {filtered.map(t => {
            const isFav = favorites.includes(t.abbr);
            return (
              <div key={t.abbr} onClick={() => toggle(t.abbr)} style={{
                display: "flex", alignItems: "center", gap: "10px", padding: "7px 8px",
                borderRadius: "4px", cursor: "pointer", background: isFav ? "rgba(137,87,229,0.08)" : "transparent",
              }}>
                <span style={{ fontSize: "14px", color: isFav ? C.star : C.starEmpty }}>{isFav ? "\u2605" : "\u2606"}</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "10px", fontWeight: 700, color: C.dim, minWidth: "36px" }}>{t.abbr}</span>
                <span style={{ fontSize: "12px", color: isFav ? C.bright : C.text, flex: 1 }}>{t.name}</span>
                <span style={{ fontSize: "10px", color: C.faint }}>{t.conf}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Conference Dropdown ───

function ConferenceDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(!open)} style={{
        fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "10px", border: "none",
        background: value ? C.pillBlue : C.pillGray, color: value ? "#fff" : C.pillGrayText, cursor: "pointer",
        display: "flex", alignItems: "center", gap: "4px",
      }}>{value || "Conference"} {"\u25BE"}</button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 300, background: C.panel, border: `1px solid ${C.border}`, borderRadius: "6px", padding: "4px", width: "180px", maxHeight: "300px", overflowY: "auto" }}>
          {["All Conferences", ...ALL_CONFERENCES].map(c => {
            const isAll = c === "All Conferences";
            const active = isAll ? !value : value === c;
            return (
              <div key={c} onClick={() => { onChange(isAll ? null : c); setOpen(false); }} style={{
                fontSize: "11px", fontWeight: active ? 600 : 400, color: active ? C.bright : C.text,
                padding: "5px 8px", borderRadius: "3px", cursor: "pointer",
                background: active ? "rgba(88,166,255,0.1)" : "transparent",
              }}>{c}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Date Picker ───

function DatePicker({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [vM, setVM] = useState(selected.getMonth());
  const [vY, setVY] = useState(selected.getFullYear());
  const ref = useRef(null);
  const today = new Date();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setVM(selected.getMonth()); setVY(selected.getFullYear()); }, [selected]);

  const isT = same(selected, today);
  const fd = new Date(vY, vM, 1).getDay();
  const dim = new Date(vY, vM + 1, 0).getDate();
  const pmd = new Date(vY, vM, 0).getDate();
  const cells = [];
  for (let i = 0; i < fd; i++) cells.push({ d: pmd - fd + 1 + i, c: false });
  for (let i = 1; i <= dim; i++) cells.push({ d: i, c: true });
  while (cells.length < 42) cells.push({ d: cells.length - fd - dim + 1, c: false });

  const ab = { background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: "12px", padding: "4px 6px" };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "2px" }}>
      <button onClick={() => onChange(addD(selected, -1))} style={ab}>{"\u25C0"}</button>
      <button onClick={() => setOpen(!open)} style={{
        fontSize: "12px", fontWeight: 500, color: C.text, background: C.pillGray,
        border: "1px solid #444c56", borderRadius: "6px", padding: "4px 10px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: "5px",
      }}>
        {fmtS(selected)}
        {isT && <span style={{ fontSize: "9px", color: C.green, fontWeight: 700 }}>TODAY</span>}
        {" \u25BE"}
      </button>
      <button onClick={() => onChange(addD(selected, 1))} style={ab}>{"\u25B6"}</button>
      {!isT && <button onClick={() => onChange(new Date())} style={{ fontSize: "10px", fontWeight: 600, color: "#fff", background: C.pillGreen, border: "none", borderRadius: "6px", padding: "3px 8px", cursor: "pointer", marginLeft: "4px" }}>Today</button>}
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200, background: C.panel, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px", width: "264px" }} onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
            <button onClick={() => { if (vM === 0) { setVM(11); setVY(vY - 1); } else setVM(vM - 1); }} style={ab}>{"\u25C0"}</button>
            <span style={{ fontSize: "13px", fontWeight: 700, color: C.bright }}>{MO[vM]} {vY}</span>
            <button onClick={() => { if (vM === 11) { setVM(0); setVY(vY + 1); } else setVM(vM + 1); }} style={ab}>{"\u25B6"}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "1px" }}>
            {["S","M","T","W","T","F","S"].map((d, i) => <div key={i} style={{ fontSize: "9px", fontWeight: 600, color: C.dim, textAlign: "center", padding: "3px 0" }}>{d}</div>)}
            {cells.map((c, i) => {
              const cd = c.c ? new Date(vY, vM, c.d) : null;
              const sel = cd && same(cd, selected);
              const tod = cd && same(cd, today);
              return (
                <button key={i} onClick={() => { if (c.c) { onChange(new Date(vY, vM, c.d)); setOpen(false); } }} disabled={!c.c} style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "11px", fontWeight: sel ? 700 : 400,
                  color: !c.c ? C.faint : sel ? "#fff" : tod ? C.green : C.text,
                  background: sel ? C.pillBlue : "transparent",
                  border: tod && !sel ? `1px solid ${C.green}` : "1px solid transparent",
                  borderRadius: "4px", padding: "5px 0", cursor: c.c ? "pointer" : "default", textAlign: "center",
                }}>{c.d}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Game Components ───

function Diamond({ runners, size = 42 }) {
  const s = size, d = s * 0.3, cx = s / 2, cy = s / 2 + 1, bs = 8;
  const bases = [
    { k: "second", x: cx, y: cy - d, on: runners.second },
    { k: "third", x: cx - d, y: cy, on: runners.third },
    { k: "first", x: cx + d, y: cy, on: runners.first },
  ];
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
      {[[cx,cy+d,cx-d,cy],[cx-d,cy,cx,cy-d],[cx,cy-d,cx+d,cy],[cx+d,cy,cx,cy+d]].map(([x1,y1,x2,y2], i) =>
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.faint} strokeWidth="1" />
      )}
      <rect x={cx-3} y={cy+d-3} width={6} height={6} fill={C.faint} transform={`rotate(45,${cx},${cy+d})`} />
      {bases.map(b => (
        <rect key={b.k} x={b.x-bs/2} y={b.y-bs/2} width={bs} height={bs} rx={1}
          fill={b.on ? "#fff" : C.page} stroke={b.on ? "#fff" : C.faint}
          strokeWidth={b.on ? 1.5 : 1} transform={`rotate(45,${b.x},${b.y})`} />
      ))}
    </svg>
  );
}

function Outs({ n }) {
  return (
    <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
      <span style={{ fontSize: "9px", color: C.dim, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, marginRight: "1px" }}>OUT</span>
      {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i < n ? "#fff" : "transparent", border: `1.5px solid ${i < n ? "#fff" : C.faint}` }} />)}
    </div>
  );
}

function CountDots({ b, s }) {
  return (
    <div style={{ display: "flex", gap: "7px", alignItems: "center" }}>
      <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
        <span style={{ fontSize: "9px", color: C.dim, fontWeight: 700, width: "8px" }}>B</span>
        {[0,1,2,3].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i < b ? "#fff" : "transparent", border: `1.5px solid ${i < b ? "#fff" : C.faint}` }} />)}
      </div>
      <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
        <span style={{ fontSize: "9px", color: C.dim, fontWeight: 700, width: "8px" }}>S</span>
        {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i < s ? "#fff" : "transparent", border: `1.5px solid ${i < s ? "#fff" : C.faint}` }} />)}
      </div>
    </div>
  );
}

function LineScore({ game }) {
  const n = Math.max(9, game.inning || 0);
  const innings = Array.from({ length: n }, (_, i) => i + 1);
  const cs = { fontFamily: "'JetBrains Mono',monospace", fontSize: "11px", textAlign: "center", padding: "3px 0", minWidth: "18px", color: C.text };
  const hc = { ...cs, color: C.dim, fontWeight: 600, fontSize: "10px" };
  const tc = { ...cs, fontWeight: 700, color: C.bright, fontSize: "12px", borderLeft: `1px solid ${C.divider}`, paddingLeft: "6px", minWidth: "22px" };

  return (
    <div style={{ overflowX: "auto", marginTop: "4px" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr>
          <td style={{ ...hc, textAlign: "left", minWidth: "42px" }}></td>
          {innings.map(i => <td key={i} style={hc}>{i}</td>)}
          <td style={{ ...hc, borderLeft: `1px solid ${C.divider}`, paddingLeft: "6px" }}>R</td>
          <td style={{ ...hc, borderLeft: `1px solid ${C.divider}`, paddingLeft: "6px" }}>H</td>
          <td style={{ ...hc, borderLeft: `1px solid ${C.divider}`, paddingLeft: "6px" }}>E</td>
        </tr></thead>
        <tbody>
          {["away", "home"].map(side => {
            const t = game[side] || {};
            const ls = t.lineScore || [];
            return (
              <tr key={side}>
                <td style={{ ...cs, textAlign: "left", fontWeight: 600, color: C.bright, fontSize: "11px" }}>{t.abbr || '?'}</td>
                {innings.map((inn, idx) => (
                  <td key={inn} style={{
                    ...cs,
                    color: ls[idx] != null ? C.text : C.faint,
                    fontWeight: game.status === "live" && inn === game.inning ? 700 : 400,
                    background: game.status === "live" && inn === game.inning ? "rgba(88,166,255,0.06)" : "transparent",
                  }}>{ls[idx] != null ? ls[idx] : "-"}</td>
                ))}
                <td style={tc}>{t.score != null ? t.score : '-'}</td>
                <td style={tc}>{t.hits != null ? t.hits : '-'}</td>
                <td style={tc}>{t.errors != null ? t.errors : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TeamLogo({ name, size = 20 }) {
  const [err, setErr] = useState(false);
  if (err || !name) return <div style={{ width: size, height: size, borderRadius: "50%", background: C.divider, flexShrink: 0 }} />;
  const url = `https://pearatings.com/api/baseball-logo/${encodeURIComponent(name)}`;
  return <img src={url} alt="" width={size} height={size} style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={() => setErr(true)} />;
}

function GameCard({ game, isFav, onToggleFav }) {
  const [exp, setExp] = useState(false);
  const isL = game.status === "live";
  const isF = game.status === "final";
  const isP = game.status === "pre";
  const isC = game.status === "cancelled";

  const innTxt = isL
    ? `${game.half === "top" ? "Top" : game.half === "bottom" ? "Bot" : game.half === "mid" ? "Mid" : "End"} ${game.inning || 1}${ord(game.inning || 1)}`
    : isF ? "FINAL" : isC ? "CANCELLED" : (game.time || game.startTime || "TBD");

  const ArrowSvg = ({ half }) => half === "top"
    ? <svg width="7" height="5" viewBox="0 0 7 5" style={{ marginRight: 2 }}><polygon points="3.5,0 7,5 0,5" fill={C.text} /></svg>
    : <svg width="7" height="5" viewBox="0 0 7 5" style={{ marginRight: 2 }}><polygon points="3.5,5 7,0 0,0" fill={C.text} /></svg>;

  return (
    <div style={{
      background: C.panel, border: `1px solid ${isL ? "rgba(46,160,67,0.35)" : C.border}`,
      borderRadius: "6px", overflow: "hidden",
    }}>
      <div onClick={() => setExp(!exp)} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "5px 10px", borderBottom: `1px solid ${C.divider}`,
        background: isL ? "rgba(46,160,67,0.04)" : "transparent", cursor: "pointer",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {isL && <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, animation: "gp 2s infinite" }} />}
          <span style={{
            fontWeight: 700, fontSize: "11px",
            color: isL ? C.green : isF ? C.dim : isC ? C.red : C.blue,
            textTransform: "uppercase", display: "flex", alignItems: "center", gap: "3px",
          }}>{isL && <ArrowSvg half={game.half} />}{innTxt}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "10px", color: C.dim }}>{game.venue}</span>
          {onToggleFav && (
            <span onClick={e => { e.stopPropagation(); onToggleFav(); }}
              style={{ fontSize: "13px", color: isFav ? C.star : C.starEmpty, cursor: "pointer" }}>
              {isFav ? "\u2605" : "\u2606"}
            </span>
          )}
        </div>
      </div>

      <div onClick={() => setExp(!exp)} style={{ display: "flex", alignItems: "stretch", padding: "8px 10px", gap: "10px", cursor: "pointer" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "5px", justifyContent: "center" }}>
          {["away", "home"].map(side => {
            const t = game[side] || {};
            const opp = game[side === "away" ? "home" : "away"] || {};
            const winning = (isL || isF) && (t.score||0) > (opp.score||0);
            const scoreColor = isP || isC ? C.dim : isL ? C.bright : winning ? C.bright : C.loser;
            const nameColor = isP || isC ? C.dim : isL ? C.bright : winning ? C.bright : C.loser;

            return (
              <div key={side} style={{ display: "flex", alignItems: "center", gap: "7px", padding: "1px 0" }}>
                <TeamLogo name={t.name} size={20} />
                <span onClick={(e) => { e.stopPropagation(); window.location.hash = `#/team/${t.slug || t.name.toLowerCase().replace(/[()]/g,'').replace(/[&]/g,'and').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`; }} style={{ fontWeight: winning && isF ? 700 : 500, fontSize: "13px", color: nameColor, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer", textDecoration: "none" }}
                  onMouseEnter={e => e.target.style.textDecoration = "underline"}
                  onMouseLeave={e => e.target.style.textDecoration = "none"}
                >{t.name}</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "18px", fontWeight: 700, color: scoreColor, minWidth: "24px", textAlign: "right", lineHeight: 1 }}>{isP || isC ? "" : (t.score != null ? t.score : "-")}</span>
              </div>
            );
          })}
        </div>
        {isL && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", minWidth: "60px", borderLeft: `1px solid ${C.divider}`, paddingLeft: "10px" }}>
            <Diamond runners={game.runners || {}} />
            <Outs n={game.outs || 0} />
            <CountDots b={game.balls || 0} s={game.strikes || 0} />
          </div>
        )}
      </div>

      {isL && game.pitcher && game.hitter && (
        <div style={{ display: "flex", gap: "12px", padding: "3px 10px 5px", borderTop: `1px solid ${C.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1 }}>
            <span style={{ fontSize: "9px", fontWeight: 700, color: C.red, background: C.redBg, padding: "1px 4px", borderRadius: "3px" }}>P</span>
            <span style={{ fontSize: "11px", color: C.text }}>{game.pitcher.name}</span>
            <span style={{ fontSize: "10px", color: C.dim }}>({game.pitcher.throws}) {game.pitcher.pitchCount}P</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1 }}>
            <span style={{ fontSize: "9px", fontWeight: 700, color: C.blue, background: C.blueBg, padding: "1px 4px", borderRadius: "3px" }}>AB</span>
            <span style={{ fontSize: "11px", color: C.text }}>{game.hitter.name}</span>
            <span style={{ fontSize: "10px", color: C.dim }}>({game.hitter.bats}) {game.hitter.avg}</span>
          </div>
        </div>
      )}

      {isF && game.wp && (
        <div style={{ display: "flex", gap: "14px", padding: "3px 10px 5px", borderTop: `1px solid ${C.divider}` }}>
          <span style={{ fontSize: "11px", color: C.green }}><b>W:</b> {game.wp.name} ({game.wp.record})</span>
          <span style={{ fontSize: "11px", color: C.red }}><b>L:</b> {game.lp.name} ({game.lp.record})</span>
          {game.sv && <span style={{ fontSize: "11px", color: C.blue }}><b>SV:</b> {game.sv.name}</span>}
        </div>
      )}

      {exp && !isP && (
        <div style={{ padding: "4px 10px 8px", borderTop: `1px solid ${C.divider}`, background: C.panelAlt }}>
          <LineScore game={game} />
        </div>
      )}
    </div>
  );
}

function SectionHeader({ text }) {
  return (
    <div style={{
      gridColumn: "1/-1", fontSize: "11px", fontWeight: 700, color: C.dim,
      textTransform: "uppercase", letterSpacing: "0.5px", padding: "12px 4px 4px",
      borderBottom: `1px solid ${C.divider}`, marginBottom: "4px",
    }}>{text}</div>
  );
}

// ─── Filter Config ───

const FILTERS = [
  { label: "All", color: null },
  { label: "Live", color: C.pillGreen },
  { label: "Final", color: C.pillGray },
  { label: "Upcoming", color: C.pillBlue },
  { label: "Ranked", color: C.pillPurple },
  { label: "\u2605 My Teams", color: "#b45309" },
];

// ─── API Config ───

// Backend URL — change this when deploying the backend
const API_URL = process.env.REACT_APP_API_URL || "https://ncaabase-api-production.up.railway.app";

async function fetchFromAPI(date) {
  const isToday = same(date, new Date());
  const endpoint = isToday ? `${API_URL}/api/scores` : `${API_URL}/api/scores/${date.toISOString().slice(0, 10)}`;
  const res = await fetch(endpoint);
  if (!res.ok) throw new Error(`API ${res.status}`);
  const data = await res.json();
  return data.games || [];
}

// ─── Team Profile Page ───

function TeamProfile({ teamSlug, onBack }) {
  const [games, setGames] = useState([]);
  const [team, setTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/team-by-slug/${teamSlug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setGames(data.games || []);
        setTeam(data.team || null);
        setTeamName(data.team?.name || data.games?.[0]?.home?.name || data.games?.[0]?.away?.name || teamSlug);
      } catch (e) {
        console.error('Failed to load team schedule:', e);
      }
      setLoading(false);
    }
    load();
  }, [teamSlug]);

  // Compute record
  const wins = games.filter(g => {
    if (g.status !== 'final') return false;
    const isHome = (g.home?.name || '').toLowerCase().includes(teamName.toLowerCase()) || teamName.toLowerCase().includes((g.home?.name || '').toLowerCase());
    const hs = g.home?.score || 0, as = g.away?.score || 0;
    return isHome ? hs > as : as > hs;
  }).length;
  const losses = games.filter(g => {
    if (g.status !== 'final') return false;
    const isHome = (g.home?.name || '').toLowerCase().includes(teamName.toLowerCase()) || teamName.toLowerCase().includes((g.home?.name || '').toLowerCase());
    const hs = g.home?.score || 0, as = g.away?.score || 0;
    return isHome ? hs < as : as < hs;
  }).length;

  const confGames = games.filter(g => g.conference);
  const confWins = confGames.filter(g => {
    if (g.status !== 'final') return false;
    const isHome = (g.home?.name || '').toLowerCase().includes(teamName.toLowerCase()) || teamName.toLowerCase().includes((g.home?.name || '').toLowerCase());
    const hs = g.home?.score || 0, as = g.away?.score || 0;
    return isHome ? hs > as : as > hs;
  }).length;
  const confLosses = confGames.filter(g => {
    if (g.status !== 'final') return false;
    const isHome = (g.home?.name || '').toLowerCase().includes(teamName.toLowerCase()) || teamName.toLowerCase().includes((g.home?.name || '').toLowerCase());
    const hs = g.home?.score || 0, as = g.away?.score || 0;
    return isHome ? hs < as : as < hs;
  }).length;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: ${C.page}; }
        ::-webkit-scrollbar-thumb { background: ${C.faint}; border-radius: 3px; }
      `}</style>
      <div style={{ minHeight: "100vh", background: C.page, color: C.text, fontFamily: "'Inter',sans-serif" }}>
        {/* Header */}
        <div style={{ background: C.header, borderBottom: `1px solid ${C.border}`, padding: "10px 20px", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", gap: "12px" }}>
            <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: "6px", padding: "4px 10px", color: C.dim, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
              ← Scores
            </button>
            <h1 style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: "17px", color: C.bright, cursor: "pointer" }} onClick={onBack}>
              <span style={{ color: C.blue }}>NCAA</span>Base
            </h1>
          </div>
        </div>

        {/* Team Header */}
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "20px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <TeamLogo name={teamName} size={56} />
            <div>
              <h2 style={{ fontSize: "22px", fontWeight: 700, color: C.bright }}>{teamName}</h2>
              <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                {team?.conf && <span style={{ fontSize: "12px", color: C.dim }}>{team.conf}</span>}
                {(wins > 0 || losses > 0) && (
                  <span style={{ fontSize: "12px", color: C.text, fontWeight: 600 }}>{wins}-{losses}</span>
                )}
                {(confWins > 0 || confLosses > 0) && (
                  <span style={{ fontSize: "12px", color: C.dim }}>({confWins}-{confLosses} conf)</span>
                )}
              </div>
            </div>
          </div>

          {/* Ratings placeholder */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "8px", marginBottom: "20px" }}>
            {[
              { label: "RPI", value: "—" },
              { label: "PEAR", value: "—" },
              { label: "SOS", value: "—" },
            ].map(r => (
              <div key={r.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: "10px", color: C.dim, fontWeight: 600, textTransform: "uppercase", marginBottom: "4px" }}>{r.label}</div>
                <div style={{ fontSize: "18px", fontWeight: 700, color: C.bright, fontFamily: "'JetBrains Mono',monospace" }}>{r.value}</div>
              </div>
            ))}
          </div>

          {/* Schedule Table */}
          <h3 style={{ fontSize: "14px", fontWeight: 700, color: C.bright, marginBottom: "10px", fontFamily: "'JetBrains Mono',monospace" }}>
            {new Date().getFullYear()} Schedule
          </h3>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: C.dim, fontSize: "13px" }}>Loading schedule...</div>
          ) : games.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: C.dim, fontSize: "13px" }}>No games found.</div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden" }}>
              {/* Table header */}
              <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 70px", padding: "8px 12px", background: C.header, borderBottom: `1px solid ${C.border}`, fontSize: "10px", fontWeight: 700, color: C.dim, textTransform: "uppercase", fontFamily: "'JetBrains Mono',monospace" }}>
                <span>Date</span>
                <span>Opponent</span>
                <span style={{ textAlign: "center" }}>Result</span>
              </div>
              {games.map((g, i) => {
                const isHome = (g.home?.name || '').toLowerCase().includes(teamName.toLowerCase()) || teamName.toLowerCase().includes((g.home?.name || '').toLowerCase());
                const opp = isHome ? g.away : g.home;
                const us = isHome ? g.home : g.away;
                const prefix = isHome ? "vs" : "@";
                const isF = g.status === 'final';
                const isL = g.status === 'live';
                const isC = g.status === 'cancelled';
                const won = isF && (us?.score||0) > (opp?.score||0);
                const lost = isF && (us?.score||0) < (opp?.score||0);

                let resultText = '';
                let resultColor = C.dim;
                if (isF) {
                  resultText = `${won ? 'W' : 'L'} ${us?.score}-${opp?.score}`;
                  resultColor = won ? C.green : C.red;
                } else if (isL) {
                  resultText = `${us?.score}-${opp?.score}`;
                  resultColor = C.blue;
                } else if (isC) {
                  resultText = 'CAN';
                  resultColor = C.red;
                } else {
                  resultText = g.time || 'TBD';
                }

                const dateStr = g.date ? `${g.date.slice(5,7)}/${g.date.slice(8,10)}` : '';

                return (
                  <div key={g.id || i} style={{
                    display: "grid", gridTemplateColumns: "80px 1fr 70px",
                    padding: "7px 12px",
                    borderBottom: i < games.length - 1 ? `1px solid ${C.divider}` : 'none',
                    fontSize: "12px",
                    background: isL ? `${C.blue}08` : 'transparent',
                  }}>
                    <span style={{ color: C.dim, fontFamily: "'JetBrains Mono',monospace", fontSize: "11px" }}>{dateStr}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: C.dim, fontSize: "11px", width: "16px" }}>{prefix}</span>
                      <TeamLogo name={opp?.name} size={16} />
                      <span
                        onClick={() => { const s = (opp?.slug || (opp?.name||'').toLowerCase().replace(/[()]/g,'').replace(/[&]/g,'and').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')); window.location.hash = `#/team/${s}`; }}
                        style={{ color: C.text, cursor: "pointer", fontSize: "12px" }}
                        onMouseEnter={e => e.target.style.textDecoration = "underline"}
                        onMouseLeave={e => e.target.style.textDecoration = "none"}
                      >
                        {opp?.rank ? `#${opp.rank} ` : ''}{opp?.name}
                      </span>
                      {g.conference && <span style={{ fontSize: "8px", color: C.dim, background: C.pillGray, padding: "1px 4px", borderRadius: "3px" }}>CONF</span>}
                      {isL && <span style={{ fontSize: "8px", color: "#fff", background: C.pillGreen, padding: "1px 5px", borderRadius: "3px", fontWeight: 700, animation: "gp 2s infinite" }}>LIVE</span>}
                    </div>
                    <span style={{ textAlign: "center", fontWeight: 600, color: resultColor, fontFamily: "'JetBrains Mono',monospace", fontSize: "11px" }}>{resultText}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: C.faint }}>
          Copyright &copy; 2026 NCAABase
        </div>
      </div>
    </>
  );
}

// ─── Main App ───

export default function App() {
  const [route, setRoute] = useState(window.location.hash || '');

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash || '');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Team profile route: #/team/team-slug
  const teamMatch = route.match(/^#\/team\/(.+)$/);
  if (teamMatch) {
    const teamSlug = decodeURIComponent(teamMatch[1]);
    return <TeamProfile teamSlug={teamSlug} onBack={() => { window.location.hash = ''; }} />;
  }

  return <Scoreboard />;
}

function Scoreboard() {
  const [filter, setFilter] = useState("All");
  const [confFilter, setConfFilter] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tick, setTick] = useState(new Date());
  const [games, setGames] = useState(MOCK_GAMES);
  const [loading, setLoading] = useState(false);
  const [apiConnected, setApiConnected] = useState(false);
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);
  const [favPanel, setFavPanel] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [defaultView, setDefaultView] = useState("all");

  // Load persisted data
  useEffect(() => {
    (async () => {
      try { const u = await storage.get("ncaabase-user"); if (u) setUser(JSON.parse(u.value)); } catch (e) {}
      try { const f = await storage.get("ncaabase-favorites"); if (f) setFavorites(JSON.parse(f.value)); } catch (e) {}
      try { const d = await storage.get("ncaabase-default-view"); if (d) setDefaultView(d.value); } catch (e) {}
    })();
  }, []);

  // Persist favorites
  useEffect(() => {
    if (user) { storage.set("ncaabase-favorites", JSON.stringify(favorites)); }
  }, [favorites, user]);

  // Persist default view
  useEffect(() => {
    if (user) { storage.set("ncaabase-default-view", defaultView); }
  }, [defaultView, user]);

  // Apply default view on login
  useEffect(() => {
    if (user && defaultView === "favorites" && favorites.length > 0) setFilter("\u2605 My Teams");
  }, [user, defaultView, favorites]);

  // Redirect fav panel to signup if not logged in
  useEffect(() => {
    if (favPanel && !user) { setFavPanel(false); setAuthModal("signup"); }
  }, [favPanel, user]);

  // Clock tick
  useEffect(() => {
    const i = setInterval(() => setTick(new Date()), 10000);
    return () => clearInterval(i);
  }, []);

  // Fetch games from API
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const apiGames = await fetchFromAPI(selectedDate);
        if (!cancelled && apiGames.length > 0) {
          setGames(apiGames);
          setApiConnected(true);
        }
      } catch (err) {
        // API not available — keep mock data (or previously loaded data)
        if (!cancelled) setApiConnected(false);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [selectedDate]);

  // Live polling — 6s when live games exist, 30s otherwise
  const hasLiveRef = useRef(false);
  hasLiveRef.current = games.some(g => g.status === "live");

  useEffect(() => {
    if (!apiConnected) return;
    let active = true;
    async function poll() {
      while (active) {
        const delay = hasLiveRef.current ? 6000 : 30000;
        await new Promise(r => setTimeout(r, delay));
        if (!active) break;
        try {
          const apiGames = await fetchFromAPI(selectedDate);
          if (apiGames.length > 0 && active) setGames(apiGames);
        } catch (err) { /* silent */ }
      }
    }
    poll();
    return () => { active = false; };
  }, [apiConnected, selectedDate]);

  const handleAuth = async (data) => {
    const userData = { name: data.name || data.username, username: data.username, email: data.email, phone: data.phone };
    setUser(userData);
    await storage.set("ncaabase-user", JSON.stringify(userData));
    setAuthModal(null);
  };

  const handleLogout = async () => {
    setUser(null);
    setFilter("All");
    await storage.delete("ncaabase-user");
  };

  // Filter & sort
  let filtered = games.filter(g => {
    if (confFilter && (g.away||{}).conf !== confFilter && (g.home||{}).conf !== confFilter) return false;
    if (filter === "Live") return g.status === "live";
    if (filter === "Final") return g.status === "final" || g.status === "cancelled";
    if (filter === "Upcoming") return g.status === "pre";
    if (filter === "Ranked") return (g.away||{}).rank || (g.home||{}).rank;
    if (filter === "\u2605 My Teams") return favorites.includes((g.away||{}).abbr) || favorites.includes((g.home||{}).abbr);
    return true;
  });
  filtered = sortGames(filtered);

  const lc = games.filter(g => g.status === "live").length;
  const liveGames = filtered.filter(g => g.status === "live");
  const preGames = filtered.filter(g => g.status === "pre");
  const finalGames = filtered.filter(g => g.status === "final" || g.status === "cancelled");

  const renderCard = (g) => {
    const gT = [(g.away||{}).abbr, (g.home||{}).abbr];
    const iF = gT.some(a => favorites.includes(a));
    return (
      <GameCard key={g.id} game={g} isFav={iF}
        onToggleFav={user ? () => {
          const uf = gT.filter(a => favorites.includes(a));
          if (uf.length > 0) setFavorites(prev => prev.filter(f => !gT.includes(f)));
          else setFavPanel(true);
        } : () => setAuthModal("signup")}
      />
    );
  };

  return (
    <>
      <style>{`
        @keyframes gp { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${C.page}; }
        ::-webkit-scrollbar-thumb { background: ${C.faint}; border-radius: 3px; }
      `}</style>
      <div style={{ minHeight: "100vh", background: C.page, color: C.text, fontFamily: "'Inter',sans-serif" }}>
        {/* Header */}
        <div style={{ background: C.header, borderBottom: `1px solid ${C.border}`, padding: "10px 20px", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ maxWidth: 960, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                <h1 style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: "17px", color: C.bright }}>
                  <span style={{ color: C.blue }}>NCAA</span>Base
                </h1>
                <span style={{ fontSize: "11px", color: C.dim }}>Live Scoreboard</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {lc > 0 && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "10px", color: "#fff", background: C.pillGreen, padding: "2px 7px", borderRadius: "10px", fontWeight: 600 }}>{lc} live</span>}
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: apiConnected ? C.green : C.faint }}>{apiConnected ? "● LIVE" : "● DEMO"}</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: C.faint }}>{tick.toLocaleTimeString()}</span>
                {user ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span onClick={() => setFavPanel(true)} style={{ fontSize: "11px", color: C.text, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                      <span style={{ width: 22, height: 22, borderRadius: "50%", background: C.pillPurple, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "10px", color: "#fff" }}>{user.name?.[0]?.toUpperCase() || "U"}</span>
                      {user.username}
                    </span>
                    <button onClick={handleLogout} style={{ fontSize: "9px", color: C.dim, background: "none", border: `1px solid ${C.border}`, borderRadius: "4px", padding: "2px 6px", cursor: "pointer" }}>Logout</button>
                  </div>
                ) : (
                  <button onClick={() => setAuthModal("login")} style={{ fontSize: "11px", fontWeight: 600, color: "#fff", background: C.pillBlue, border: "none", borderRadius: "6px", padding: "4px 10px", cursor: "pointer" }}>Sign In</button>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
              <DatePicker selected={selectedDate} onChange={setSelectedDate} />
              <ConferenceDropdown value={confFilter} onChange={setConfFilter} />
              <div style={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
                {FILTERS.map(f => {
                  if (f.label === "\u2605 My Teams" && !user) return null;
                  const active = filter === f.label;
                  return (
                    <button key={f.label} onClick={() => {
                      if (f.label === "\u2605 My Teams" && favorites.length === 0) { setFavPanel(true); return; }
                      setFilter(f.label);
                    }} style={{
                      fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "10px", border: "none",
                      background: active ? (f.color || C.pillBlue) : C.pillGray,
                      color: active ? "#fff" : C.pillGrayText, cursor: "pointer",
                    }}>
                      {f.label}
                      {f.label === "Live" && lc > 0 && ` (${lc})`}
                      {f.label === "\u2605 My Teams" && favorites.length > 0 && ` (${favorites.length})`}
                    </button>
                  );
                })}
                {user && <button onClick={() => setFavPanel(true)} style={{ fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "10px", border: `1px dashed ${C.border}`, background: "transparent", color: C.dim, cursor: "pointer" }}>Edit Teams</button>}
              </div>
            </div>
          </div>
        </div>

        {/* Game Grid */}
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "14px 14px 28px", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(440px,1fr))", gap: "8px" }}>
          {loading && games.length === 0 && (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "60px 20px", color: C.dim, fontSize: "13px" }}>Loading scores...</div>
          )}
          {filter === "All" && liveGames.length > 0 && <SectionHeader text="Live" />}
          {filter === "All" && liveGames.map(renderCard)}
          {filter === "All" && preGames.length > 0 && <SectionHeader text="Upcoming" />}
          {filter === "All" && preGames.map(renderCard)}
          {filter === "All" && finalGames.length > 0 && <SectionHeader text="Final" />}
          {filter === "All" && finalGames.map(renderCard)}
          {filter !== "All" && filtered.map(renderCard)}
          {filtered.length === 0 && !loading && (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "60px 20px" }}>
              <div style={{ color: C.dim, fontSize: "13px", marginBottom: "8px" }}>
                {filter === "\u2605 My Teams" ? "No games found for your favorite teams today." : "No games match the selected filters."}
              </div>
              {filter === "\u2605 My Teams" && (
                <button onClick={() => setFavPanel(true)} style={{ fontSize: "12px", fontWeight: 600, color: "#fff", background: C.pillPurple, border: "none", borderRadius: "6px", padding: "6px 14px", cursor: "pointer" }}>Edit My Teams</button>
              )}
            </div>
          )}
        </div>

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "'JetBrains Mono',monospace", fontSize: "9px", color: C.faint }}>
          Copyright &copy; 2026 NCAABase
        </div>

        {authModal && <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onAuth={handleAuth} onSwitch={() => setAuthModal(authModal === "login" ? "signup" : "login")} />}
        {favPanel && user && <FavoritesPanel favorites={favorites} setFavorites={setFavorites} defaultView={defaultView} setDefaultView={setDefaultView} onClose={() => setFavPanel(false)} />}
      </div>
    </>
  );
}
